import { Fragment, type FC } from "react"

import {
  MODULE_POWUR_OS_EQUIPMENT_ID,
  INVERTER_POWUR_OS_EQUIPMENT_ID,
  ENERGY_BACKUP_POWUR_OS_EQUIPMENT_ID,
} from "@logic/lib/powur-os"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { useRecoilValue } from "recoil"

import { EquipmentSelection } from "~/components/Icons"

import { NOTIFICATION_COLORS, NotificationMessage } from "./notifications"
import { poisonedDesignState } from "../designs/design-state"
import { useProposalContext } from "../proposal/proposal-context"

// [eyecue-codemap-group:FUDqywtEK4S]
const EquipmentForSiteConstraintName = `Equipment For Site`
// [end-eyecue-codemap-group:FUDqywtEK4S:d99d861bc24acf68183465ad761ef70c6edf5139]

// [eyecue-codemap-group:XxAPuG42DQN]
const AllowedUtilitiesConstraintName = `Equipment Allowed Utilities`
// [end-eyecue-codemap-group:XxAPuG42DQN:7842bd9a37d9ef5f26e4cf39bbac3a767162cc1e]

const isFromConstraint = (
  pd: unknown,
): pd is { data: { constraintName: string }; reason: string }[] =>
  Boolean(
    pd &&
      Array.isArray(pd) &&
      pd.every((d) => `data` in d && d?.data?.constraintName) &&
      pd.every((d) => `reason` in d),
  )

const splitReasonOnNewLine = (reason: string) =>
  reason.split(`\n`).map((line, i) => (
    <p key={i} style={{ minHeight: `10px` }}>
      {line}
    </p>
  ))

export const PoisonDesignMessages: FC = () => {
  const { t } = useTranslation()
  const poisonedDesign = useRecoilValue(poisonedDesignState)

  if (!poisonedDesign) return null

  const Warning = (
    <>
      <div data-role="divider" />
      <p>
        Savings could not be calculated. Please resolve the constraint error to
        enable savings calculation.
      </p>
    </>
  )

  const poisonData = poisonedDesign.data
  if (isFromConstraint(poisonData)) {
    const messages = poisonData.map((poison, index) => {
      const { data } = poison

      return (
        <Fragment key={`${data.constraintName}-${index}`}>
          {splitReasonOnNewLine(poison.reason)}
          <PoisonLink poisonData={data} />
        </Fragment>
      )
    })

    return (
      <NotificationMessage {...NOTIFICATION_COLORS.danger}>
        <p>{t(`poisonedDesign`)}</p>
        <div data-role="divider" />
        {messages}
        {Warning}
      </NotificationMessage>
    )
  }

  return (
    <NotificationMessage {...NOTIFICATION_COLORS.danger}>
      <p>{t(`designNeedsCorrected`)}</p>
      <div data-role="divider" />
      {splitReasonOnNewLine(poisonedDesign.reason)}
      {Warning}
    </NotificationMessage>
  )
}

export const PoisonLink: FC<{
  poisonData: {
    constraintName: string
    [key: string]: number | string | undefined
  }
}> = ({ poisonData }) => {
  const navigate = useNavigate()
  const { slug } = useProposalContext().local
  const { t } = useTranslation()

  const { constraintName, ...constraintSpecificValues } = poisonData
  if (!constraintName) return null

  switch (constraintName) {
    case AllowedUtilitiesConstraintName:
    case EquipmentForSiteConstraintName: {
      const link = getEquipmentSelectionLink(
        constraintSpecificValues[`powurOsEquipmentId`] as number,
        slug,
      )

      if (link === null) return null

      return (
        <button data-role="link" onClick={() => navigate(link)}>
          ({constraintSpecificValues[`name`] as string})
        </button>
      )
    }
    case `Texas REP Plans Temp Constraint`:
      return (
        <>
          <button
            data-role="link"
            onClick={() =>
              navigate(
                `/proposal/${slug}/setup/home-and-utility-info/utility-company-and-rate`,
              )
            }
            style={{ marginTop: `5px` }}
          >
            <EquipmentSelection />
            {t(`updateUtilityInfo`)}
          </button>
        </>
      )
    default:
      return null
  }
}

export const getEquipmentSelectionLink = (
  powurOsEquipmentId: number | undefined,
  slug: string,
): string | null => {
  const powurOsEquipmentIdToType: Record<number, string> = {
    [MODULE_POWUR_OS_EQUIPMENT_ID]: `module-selection`,
    [INVERTER_POWUR_OS_EQUIPMENT_ID]: `inverter-selection`,
    [ENERGY_BACKUP_POWUR_OS_EQUIPMENT_ID]: `storage-selection`,
  }

  if (!powurOsEquipmentId) return null

  const type = powurOsEquipmentIdToType[powurOsEquipmentId]

  if (!type) return null

  return `/proposal/${slug}/setup/equipment-selection/${type}`
}
